import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { ToolbarContainer } from 'components/Containers/ToolbarContainer';
import { Selector } from 'components/Form/Selectors/Selector';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useState } from 'react';
import { selectLatestTagData } from 'store/device.slice';
import { useAppSelector } from 'store/hook';
import { selectNotificationsData } from 'store/notification.slice';
import {
  type AssetsOverview,
  type CumulativeOverview,
  type EolDates,
  type ServiceView,
} from 'types/survivalAnalysis.types';
import { timeAgo } from 'views/FleetOverview/components/AssetCardsV2/utils';
import AssetOrComponentCard from 'views/SurvivalAnalysis/Components/AssetOrComponentCard';
import {
  getAssetImage,
  processAssetsWithLiveData,
  processDataAssets,
} from 'views/SurvivalAnalysis/Helper/Function/asset.helper.function';
import OverviewModalDetails from './Overview-modal-details';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const CardView = ({
  asset,
  onShowDetails,
}: {
  asset: ServiceView;
  onShowDetails: (params: ServiceView) => void;
}) => {
  const { t } = useTranslation();
  const assetData = useMemo(() => {
    const temp: {
      imageUrl: string;
      title: string;
      subtitle: string;
      risks: {
        high: { value: number | string; label: string };
        medium: { value: number | string; label: string };
        low: { value: number | string; label: string };
      };
      details: Array<{ label: string; value: string }>;
    } = {
      imageUrl: asset.type ? getAssetImage(asset.type) : '',
      title: asset.item_id ?? 'N/A',
      subtitle: `OEM: ${asset.oem ?? 'N/A'}, Model: ${asset.model ?? 'N/A'}`,
      risks: {
        high: { value: asset.high ?? 'N/A', label: t('high_risk') },
        medium: { value: asset.med ?? 'N/A', label: t('med_risk') },
        low: { value: asset.low ?? 'N/A', label: t('low_risk') },
      },
      details: [],
    };
    temp.details.push({
      label: t('type'),
      value: asset.type
        ? asset.type?.split('_')?.join(' ').toUpperCase()
        : 'N/A',
    });
    temp.details.push({
      label: t('last_connected'),
      value: asset.lastConnected ? timeAgo(asset.lastConnected) : 'N/A',
    });

    return temp;
  }, [asset]);

  const handleClickViewDetails = () => {
    onShowDetails(asset);
  };

  return (
    <AssetOrComponentCard
      {...assetData}
      onViewDetails={handleClickViewDetails}
    />
  );
};
export const OverviewModalAsset = ({
  enableDarkTheme,
  allAssets,
  assetsPerSubComponent,
  selectedAssetFromTimeLine,
  eolDates,
  onOpenInNewClick,
  handleModalSuccess,
}: {
  enableDarkTheme: boolean;
  allAssets: AssetsOverview[];
  assetsPerSubComponent: CumulativeOverview[];
  selectedAssetFromTimeLine?: string;
  eolDates: EolDates[];
  onOpenInNewClick: (itemId: string) => void;
  handleModalSuccess: () => void;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [sortCriteria, setSortCriteria] = useState('2');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAssets, setFilteredAssets] = useState<ServiceView[]>([]);
  const [processedData, setProcessedData] = useState<ServiceView[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<ServiceView>();
  const { assets } = useAppSelector((state) => state.assetReducer);
  const latestTagData = useAppSelector(selectLatestTagData);
  const notificationsData = useAppSelector(selectNotificationsData);

  useEffect(() => {
    if (selectedAssetFromTimeLine) {
      const assetWithLiveData = processAssetsWithLiveData(
        assets,
        latestTagData,
        notificationsData
      );
      const processedData = processDataAssets(
        allAssets,
        assetWithLiveData,
        eolDates
      );
      const filtered = processedData.filter(
        (asset) => asset.item_id === selectedAssetFromTimeLine
      );
      setSelectedAsset(filtered[0]);
    }
    onOpenInNewClick('');
  }, [selectedAssetFromTimeLine]);

  type CategoryKey = 'eol' | 'high' | 'med' | 'low';

  const sortCriteriaMap: Record<number, CategoryKey> = {
    2: 'high',
    3: 'med',
    4: 'low',
  };

  useEffect(() => {
    const filterAndSortAssets = () => {
      // Ensure sortCriteria is treated as a number
      const categoryKey = sortCriteriaMap[Number(sortCriteria)];
      const filtered = processedData.filter((asset) =>
        asset.item_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      filtered.sort((a, b) => {
        // Using nullish coalescing operator to handle undefined or null values
        const aCategoryValue = a[categoryKey] ?? 0;
        const bCategoryValue = b[categoryKey] ?? 0;
        return +bCategoryValue - +aCategoryValue; // Sort descending
      });
      setFilteredAssets(filtered);
    };

    filterAndSortAssets();
  }, [searchTerm, sortCriteria, processedData]); // Depend on filteredAssets, sortCriteria, and searchTerm

  useEffect(() => {
    const assetWithLiveData = processAssetsWithLiveData(
      assets,
      latestTagData,
      notificationsData
    );
    const processedData = processDataAssets(
      allAssets,
      assetWithLiveData,
      eolDates
    );
    setProcessedData(processedData);
  }, [allAssets]);

  const onShowDetails = (asset: ServiceView) => {
    setSelectedAsset(asset); // Set the selected asset
  };

  if (selectedAsset) {
    return (
      <OverviewModalDetails
        displayItems={selectedAsset}
        uniqueAssets={filteredAssets}
        onShowDetails={onShowDetails}
        detailType={t('assets')}
        handleModalSuccess={handleModalSuccess}
      />
    );
  }

  return (
    <>
      <ToolbarContainer enableDarkTheme={enableDarkTheme}>
        <Box
          width={'100%'}
          display={'flex'}
          flexDirection={'row'}
          gap={2}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Box
            display={'flex'}
            flexDirection={'row'}
            gap={2}
            alignItems={'center'}
          >
            <Typography
              fontStyle={{
                color: palette.textPrimary,
              }}
              fontSize={'20px'}
            >
              {t('all_assets')}
            </Typography>
          </Box>

          <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'end'}
            gap={2}
          >
            <TextField
              variant="outlined"
              size="small"
              placeholder={t('search_for_an_asset')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              sx={{
                width: '400px',
              }}
              InputProps={{
                sx: {
                  height: '38px',
                  minWidth: '140px',
                  backgroundColor: enableDarkTheme
                    ? palette.menuOptions
                    : palette.boxBackground,
                  color: palette.textPrimary,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      style={{
                        color: enableDarkTheme
                          ? ThemePalette.typography.white
                          : ThemePalette.typography.grey,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Selector
              enableDarkTheme={enableDarkTheme}
              minWidth="150px"
              height="20px"
              selectSx={{
                width: '200px',
              }}
              value={parseInt(sortCriteria)}
              selectorOptions={[
                { id: '0', display: 'Sort by Default' },
                { id: '2', display: 'Sort by High Risk' },
                { id: '3', display: 'Sort by Medium Risk' },
                { id: '4', display: 'Sort by Low Risk' },
              ]}
              onChange={(event) => {
                const selectedValue = event.target.value as string;
                setSortCriteria(selectedValue);
              }}
            />
          </Box>
        </Box>
      </ToolbarContainer>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '24px',
          margin: '16px 0px',
        }}
      >
        {filteredAssets.length > 0 ? (
          filteredAssets?.map((el: ServiceView) => (
            <CardView
              asset={el}
              key={`${el.item_id}-${el?.assetId}`}
              onShowDetails={onShowDetails}
            />
          ))
        ) : (
          <div
            style={{
              width: '100%',
              height: '65vh',
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
              columnSpan: 'all',
              gridColumnStart: 0,
              gridColumnEnd: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
            }}
          ></div>
        )}
      </div>
    </>
  );
};
